import { useState, useEffect, useRef, useCallback } from "react";
import { Bot, X, Send, Loader2, Sparkles, RotateCcw, Mic, ArrowRight, MicOff, Radio, Volume2, ExternalLink, Zap, Plus, Trash2, ThumbsUp, ThumbsDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import VoiceRecordButton from "./VoiceRecordButton";

// Rollenbasierte Schnell-Vorschläge (Chips) — senken die Einstiegshürde beim Öffnen.
// Optionale `to`/`ctaLabel` blenden nach der Frage einen direkten Aktions-Button ein
// (Hilfe-Frage → konkrete Conversion).
const SUGGESTIONS = {
    admin: [
        { text: "Wie viele offene Tickets gibt es?", ctaLabel: "Zu den Tickets", to: "/admin/tickets" },
        { text: "Gib mir eine Übersicht der Plattform-Kennzahlen" },
        { text: "Welche Händler haben die meisten Inserate?" },
    ],
    b2b: [
        { text: "Wie viele Inserate habe ich?" },
        { text: "Wie lege ich ein neues Inserat an?", ctaLabel: "Jetzt Inserat erstellen", to: "/create-listing" },
        { text: "Zeig mir meine offenen Tickets", ctaLabel: "Zu meinen Tickets", to: "/dashboard?tab=settings" },
        { text: "Wie funktioniert die Disposition?" },
    ],
    zweiradverkaeufer: [
        { text: "Wie lege ich ein Inserat an?", ctaLabel: "Jetzt Inserat erstellen", to: "/create-listing" },
        { text: "Wie erfasse ich einen Verkauf?" },
        { text: "Wo finde ich die Werkstatt-Aufträge?", ctaLabel: "Zu den Werkstatt-Aufträgen", to: "/dealer/workshop-orders" },
    ],
    _default: [
        { text: "Wie funktioniert die Plattform?" },
        { text: "Wie sende ich einen Preisvorschlag?", ctaLabel: "Fahrzeuge ansehen", to: "/search" },
        { text: "Wie kontaktiere ich einen Händler?", ctaLabel: "Fahrzeuge ansehen", to: "/search" },
    ],
};

// Schlüsselwort-Erkennung für FREIE Texteingaben (auch Voice) — blendet automatisch
// die passende CTA ein, unabhängig von den Vorschlags-Chips. `roles` grenzt ein,
// für wen die Aktion sinnvoll ist (leer = alle Rollen).
const KEYWORD_CTAS = [
    { re: /inserat|anzeige|fahrzeug.*(einstell|inserier|anleg)|verkauf.*(einstell|inserier)/i, label: "Jetzt Inserat erstellen", to: "/create-listing", roles: ["b2b", "zweiradverkaeufer"] },
    { re: /werkstatt|reparatur|werkstattauftrag|hebebühne/i, label: "Zu den Werkstatt-Aufträgen", to: "/dealer/workshop-orders", roles: ["b2b", "zweiradverkaeufer"] },
    { re: /ticket|support|störung|problem melden|fehler melden/i, label: "Zu den Tickets", to: "/admin/tickets", roles: ["admin"] },
    { re: /ticket|support|störung|problem melden|fehler melden/i, label: "Zu meinen Tickets", to: "/dashboard?tab=settings", roles: ["b2b", "zweiradverkaeufer"] },
    { re: /preisvorschlag|angebot senden|fahrzeug.*(such|find|kauf)|motorrad.*(such|find|kauf)/i, label: "Fahrzeuge ansehen", to: "/search" },
];

function detectCta(text, role) {
    for (const k of KEYWORD_CTAS) {
        if ((!k.roles || k.roles.includes(role)) && k.re.test(text)) {
            return { label: k.label, to: k.to };
        }
    }
    return null;
}

// Proaktives Verhalten: kontextbezogene Vorschläge je nach aktueller Seite.
// Beim Öffnen auf z. B. der Werkstatt-Seite bietet der Assistent direkt passende
// Hilfen an („Brauchst du Hilfe beim Kaufvertrag?") → echter Begleiter.
function detectPageContext(pathname) {
    if (/\/create-listing|\/edit-listing/.test(pathname)) return {
        greeting: "Du erstellst gerade ein Inserat — soll ich dir dabei helfen?",
        nudge: "Komme ich beim Inserat-Erstellen helfen?",
        suggestions: [
            { text: "Wie fülle ich die Pflichtfelder richtig aus?" },
            { text: "Wie funktioniert die KI-Preisempfehlung?" },
            { text: "Wie lade ich gute Fahrzeugfotos hoch?" },
        ],
    };
    if (/\/dealer\/workshop/.test(pathname)) return {
        greeting: "Annahme & Auftrag — ich helfe beim Anlegen des Auftrags und beim Erfassen von Kunden- & Fahrzeugdaten.",
        nudge: "Hilfe beim Auftrag oder bei den Kunden-/Fahrzeugdaten?",
        restrict: "order_intake",
        suggestions: [
            { text: "Wie lege ich einen Werkstattauftrag an?" },
            { text: "Welche Kundendaten muss ich erfassen?" },
            { text: "Welche Fahrzeugdaten brauche ich für den Auftrag?" },
        ],
    };
    if (/\/dealer\/wms/.test(pathname)) return {
        greeting: "Du bist in der Warenwirtschaft — wie kann ich helfen?",
        nudge: "Fragen zur Warenwirtschaft?",
        suggestions: [
            { text: "Wie lege ich einen Artikel an?" },
            { text: "Wie funktioniert die VK-Kalkulation?" },
            { text: "Wie buche ich einen Wareneingang?" },
        ],
    };
    if (/\/listing\//.test(pathname)) return {
        greeting: "Brauchst du Hilfe bei diesem Fahrzeug?",
        nudge: "Hilfe beim Kaufvertrag oder der Abwicklung?",
        suggestions: [
            { text: "Wie wickle ich einen Verkauf ab (Disposition)?" },
            { text: "Wie erstelle ich den Kaufvertrag?" },
            { text: "Wie erfasse ich eine Inzahlungnahme?" },
        ],
    };
    return null;
}

/**
 * ChatbotWidget (Feb-2026, User-Wunsch) — schwebender KI-Assistent (unten rechts).
 * Rollenbasiert eingegrenzt (Backend bestimmt den Zugriff). Manuelle Texteingabe
 * ODER Spracheingabe; bietet aktiv an, das Anliegen per Sprache zu besprechen.
 */
// Läuft die App in einem eingebetteten iframe (z. B. Emergent-Vorschau ohne
// allow="microphone")? Dann blockiert die Permissions-Policy das Mikrofon —
// unabhängig von der Browser-Berechtigung.
function isEmbedded() {
    try { return window.self !== window.top; } catch { return true; }
}

// Android (Chrome): Mikrofon ist EXKLUSIV — Web-Speech-Erkennung und
// MediaRecorder können nicht gleichzeitig aufnehmen; SR wirft dann
// fälschlich „not-allowed", obwohl der Zugriff erteilt ist.
const IS_ANDROID = /android/i.test(navigator.userAgent || "");

// Jul-2026 — Toleranter Wake-Word-Match für „Bikevinci" (fängt Aussprache-
// und Erkennungs-Varianten ab: „bike vinci", „beik winzi", „vinci", „wintschi" …)
const WAKE_RE = /(bike?|by|bei|baik|beik|pike)[\s-]?(vin|win|fin)[cszt]{0,3}[iy]|\b(vinci|vinzi|winzi|wintschi|vintschi|finci)\b/;

// Jul-2026 (User-Wunsch) — eigenes, einfacheres Aktivierungswort (Standard
// „Hey Martin"), zusätzlich zu „Bikevinci". Im Widget einstellbar (localStorage).
const DEFAULT_WAKE_WORD = "Hey Martin";

// Jul-2026 (User-Wunsch) — Stimme passend zum Assistenten-Namen: „Hey Martin"
// spricht mit Männerstimme (onyx), „Hallo Bikevinci" & Co. behalten die
// bisherige Frauenstimme (nova).
function getTtsVoice() {
    try {
        const w = (localStorage.getItem("tw_wake_word") || DEFAULT_WAKE_WORD).toLowerCase();
        return w.includes("martin") ? "onyx" : "nova";
    } catch { return "nova"; }
}
function wakeRegexFor(word) {
    const parts = String(word || "").toLowerCase().trim().split(/\s+/)
        .map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).filter(Boolean);
    if (!parts.length) return null;
    return new RegExp(`\\b${parts.join("[\\s,-]*")}\\b`, "i");
}

export default function ChatbotWidget() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // Aug-2026 (User-Wunsch) — KI-Button verschiebbar (Handy + PC), falls er
    // mal etwas verdeckt; Position wird gemerkt.
    const [fabPos, setFabPos] = useState(() => {
        try { return JSON.parse(localStorage.getItem("tw_chatbot_pos") || "null"); } catch { return null; }
    });
    const lastDragRef = useRef(0);
    const onFabPointerDown = (e) => {
        if (e.button != null && e.button !== 0) return;
        const startX = e.clientX, startY = e.clientY;
        const rect = e.currentTarget.getBoundingClientRect();
        let moved = false;
        const move = (ev) => {
            const dx = ev.clientX - startX, dy = ev.clientY - startY;
            if (!moved && Math.hypot(dx, dy) < 6) return;
            moved = true;
            ev.preventDefault();
            const r = Math.min(Math.max(window.innerWidth - (rect.right + dx), 8), window.innerWidth - rect.width - 8);
            const b = Math.min(Math.max(window.innerHeight - (rect.bottom + dy), 8), window.innerHeight - rect.height - 8);
            setFabPos({ r: Math.round(r), b: Math.round(b) });
        };
        const up = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
            if (moved) {
                lastDragRef.current = Date.now();
                setFabPos((p) => {
                    try { localStorage.setItem("tw_chatbot_pos", JSON.stringify(p)); } catch { /* noop */ }
                    return p;
                });
            }
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };
    const fabStyle = fabPos ? { right: fabPos.r, bottom: fabPos.b, touchAction: "none" } : { touchAction: "none" };
    const panelStyle = fabPos ? {
        right: Math.min(fabPos.r, Math.max(8, window.innerWidth - 400)),
        bottom: Math.min(fabPos.b, Math.max(8, window.innerHeight - 590)),
    } : undefined;

    // Rechts-Dialog (LegalReconfirm) fordert Vorrang an → Chat-Panel schließen.
    useEffect(() => {
        const close = () => setOpen(false);
        window.addEventListener("tw-close-chatbot", close);
        return () => window.removeEventListener("tw-close-chatbot", close);
    }, []);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    // Jul-2026 (User-Wunsch) — Daumen hoch/runter Feedback pro KI-Antwort.
    const [msgFeedback, setMsgFeedback] = useState({});
    const sendFeedback = async (i, rating, text) => {
        setMsgFeedback((f) => ({ ...f, [i]: rating }));
        try { await api.post("/assistant/feedback", { rating, message_text: text || "", session_id: sessionId }); } catch { /* noop */ }
    };
    const [scope, setScope] = useState("");
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [cta, setCta] = useState(null); // { label, to } — kontextbezogene Aktion
    const [nudge, setNudge] = useState(false); // dezenter Inaktivitäts-Hinweis
    const endRef = useRef(null);
    // Jun-2026 (User-Wunsch): Transkriptions-Mikrofon standardmäßig ausgeblendet,
    // per Schalter im Kopfbereich einblendbar.
    const [showTranscribe, setShowTranscribe] = useState(() => {
        try { return localStorage.getItem("tw_transcribe_visible") === "1"; } catch { return false; }
    });
    const toggleTranscribe = () => {
        setShowTranscribe((v) => {
            const next = !v;
            try { localStorage.setItem("tw_transcribe_visible", next ? "1" : "0"); } catch { /* */ }
            return next;
        });
    };
    // --- Sprachaktivierung „Bikevinci" (Wake-Word + Sprachantwort) · Jun 2026 ---
    const [voiceWake, setVoiceWake] = useState(() => {
        try { return localStorage.getItem("tw_bikevinci_wake") === "1"; } catch { return false; }
    });
    const [voiceListening, setVoiceListening] = useState(false); // hört aktiv auf eine Frage
    const [voiceSpeaking, setVoiceSpeaking] = useState(false);   // KI spricht gerade
    const [micState, setMicState] = useState("unknown"); // unknown|granted|denied|blocked|unsupported|nodevice|insecure|error
    const [showMicHelp, setShowMicHelp] = useState(false);
    const recognitionRef = useRef(null);
    const modeRef = useRef("wake");      // wake | convo | busy
    const convoTimerRef = useRef(null);  // Stille-Timeout im Freihand-Gespräch
    const startInConvoRef = useRef(false); // Freihand-Start: direkt zuhören (ohne Wake-Word)
    const speakingRef = useRef(false);
    const voiceWakeRef = useRef(voiceWake);
    const sendRef = useRef(null);
    const openRef = useRef(null);
    const processRef = useRef(null);
    // Jul-2026 (User-Wunsch) — konfigurierbares Aktivierungswort („Hey Martin").
    const [wakeWord, setWakeWord] = useState(() => {
        try { return localStorage.getItem("tw_wake_word") || DEFAULT_WAKE_WORD; } catch { return DEFAULT_WAKE_WORD; }
    });
    const customWakeRef = useRef(wakeRegexFor(wakeWord));
    useEffect(() => {
        customWakeRef.current = wakeRegexFor(wakeWord);
        try { localStorage.setItem("tw_wake_word", wakeWord); } catch { /* */ }
    }, [wakeWord]);
    const wakeTest = (t) => WAKE_RE.test(t) || Boolean(customWakeRef.current && customWakeRef.current.test(t));
    const wakeStrip = (t) => {
        let out = t.replace(WAKE_RE, "");
        if (customWakeRef.current) out = out.replace(customWakeRef.current, "");
        return out;
    };

    const loadSession = useCallback(async () => {
        try {
            const { data } = await api.get("/assistant/session");
            setScope(data?.scope || "");
            if (data?.session) {
                setSessionId(data.session.id);
                setMessages(data.session.messages || []);
            } else if (!user) {
                // Gast: Session-Id lokal fortführen (Backend speichert unter user_id=null)
                try { const sid = localStorage.getItem("tw_assistant_guest_session"); if (sid) setSessionId(sid); } catch { /* noop */ }
            }
        } catch { /* ignore */ }
        finally { setLoaded(true); }
    }, [user]);

    useEffect(() => { if (open && !loaded) loadSession(); }, [open, loaded, loadSession]);
    useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: "nearest" }); }, [messages.length, open]);

    // Dezenter Inaktivitäts-Nudge: auf einer Kontext-Seite nach 20 s einmal pro
    // Session sanft auf den Assistenten aufmerksam machen.
    // Jul-2026 (User-Wunsch): Das X blendet die Fragen DAUERHAFT aus (Opt-out),
    // nicht nur auf der aktuellen Seite.
    const NUDGE_OPTOUT_KEY = "tw_chatbot_nudge_optout";
    const dismissNudge = () => {
        try { localStorage.setItem(NUDGE_OPTOUT_KEY, "1"); } catch { /* noop */ }
        track("nudge_dismiss", pageCtx?.nudge);
        setNudge(false);
    };
    useEffect(() => {
        setNudge(false);
        if (open) return;
        try { if (localStorage.getItem(NUDGE_OPTOUT_KEY)) return; } catch { /* noop */ }
        const ctx = detectPageContext(location.pathname);
        if (!ctx) return;
        const key = `tw_chatbot_nudge_${location.pathname}`;
        if (sessionStorage.getItem(key)) return;
        const t = setTimeout(() => {
            setNudge(true);
            sessionStorage.setItem(key, "1");
            if (user) api.post("/assistant/track", { event_type: "nudge_shown", label: ctx.nudge, page: location.pathname }).catch(() => { /* noop */ });
        }, 20000);
        return () => clearTimeout(t);
    }, [location.pathname, open, user]);

// Jun-2026 (User-Wunsch) — Bikevinci-Aktionen: Seiten auf Zuruf öffnen
// („öffne die Werkstattaufträge", „neues Inserat", „zeig mir die Rechnungen" …).
const NAV_TARGETS = [
    [/werkstatt\s?auftr|werkstattauftr/, "/dealer/workshop-orders", "die Werkstattaufträge"],
    [/neues? inserat|inserat (erstellen|anlegen)|fahrzeug (einstellen|inserieren)/, "/create-listing", "ein neues Inserat"],
    [/werkstatt\s?kalender|kalender/, "/dealer/workshop-calendar", "den Werkstattkalender"],
    [/rechnung/, "/dealer/invoices", "die Rechnungen"],
    [/kunden/, "/dealer/invoices?tab=customers", "die Kundenverwaltung"],
    [/disposition|dispo\b/, "/dealer/disposition", "die Disposition"],
    [/e-?mail|postfach/, "/dealer/email", "das E-Mail-Center"],
    [/buchhaltung/, "/dealer/buchhaltung", "die Buchhaltung"],
    [/warenwirtschaft|wms|lager\b/, "/dealer/wms", "die Warenwirtschaft"],
    [/mitarbeiter/, "/dealer/employees", "die Mitarbeiterverwaltung"],
    [/leasing/, "/dealer/leasingrueckgabe", "die Leasingrückgaben"],
    [/winterlager/, "/dealer/winterlager", "das Winterlager"],
    [/statistik|auswertung|analytics/, "/dealer/analytics", "die Auswertungen"],
    [/dashboard|übersicht|cockpit/, "/dashboard", "das Dashboard"],
];

function matchNavIntent(text) {
    const lc = (text || "").toLowerCase().trim();
    if (/^neues? inserat\b/.test(lc)) return { path: "/create-listing", label: "ein neues Inserat" };
    if (!/(öffne|oeffne|zeige?( mir)?|geh(e)? (zu|in|auf)|navigier|bring mich)/.test(lc)) return null;
    for (const [re, path, label] of NAV_TARGETS) {
        if (re.test(lc)) return { path, label };
    }
    return null;
}

// Jul-2026 (User-Wunsch) — SSE-Stream vom Backend konsumieren: liefert die
// Antwort tokenweise (onFull erhält den bisherigen Gesamttext).
async function streamChat(payload, onFull) {
    const token = sessionStorage.getItem("tw_token");
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
    });
    if (!resp.ok || !resp.body) throw new Error("stream_failed");
    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = "", full = "";
    const meta = {};
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf("\n\n")) >= 0) {
            const line = buf.slice(0, i).trim();
            buf = buf.slice(i + 2);
            if (!line.startsWith("data:")) continue;
            let ev;
            try { ev = JSON.parse(line.slice(5)); } catch { continue; }
            if (ev.session_id) meta.session_id = ev.session_id;
            if (ev.scope) meta.scope = ev.scope;
            if (ev.delta) { full += ev.delta; onFull(full); }
            if (ev.done) meta.reply = ev.reply || full;
        }
    }
    meta.reply = (meta.reply || full).trim();
    if (!meta.reply) throw new Error("empty_stream");
    return meta;
}

// Jul-2026 — Text für die Sprachausgabe bereinigen: Markdown, Emojis,
// Sonderzeichen und URLs werden NICHT vorgelesen.
// Jul-2026 (User-Wunsch) — Navigations-Marker der KI: [[NAV:/pfad|Label]] zeigt
// einen Knopf, [[NAV!:/pfad|Label]] wechselt sofort die Seite.
function parseNavMarker(text) {
    const s = String(text || "");
    const m = s.match(/\[\[NAV(!?):([^\]|]+)(?:\|([^\]]+))?\]\]/);
    const clean = s.replace(/\s*\[\[NAV!?:[^\]]*\]\]\s*/g, " ").replace(/\s*\[\[NAV[^\]]*$/, "").trim();
    if (!m) return { clean, nav: null };
    const path = m[2].trim();
    if (!path.startsWith("/")) return { clean, nav: null };
    return { clean, nav: { auto: m[1] === "!", path, label: (m[3] || "Seite öffnen").trim() } };
}

function sanitizeForSpeech(text) {
    return String(text || "")
        .replace(/\[\[NAV!?:[^\]]*\]\]/g, " ")
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`([^`]*)`/g, "$1")
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/https?:\/\/\S+/g, "")
        .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/gu, " ")
        .replace(/[*_#~^<>|•●▪§±=]+/g, " ")
        .replace(/[-–—]{2,}/g, ", ")
        .replace(/\s*\n+\s*/g, ". ")
        .replace(/\s{2,}/g, " ")
        .trim();
}

// Antwort in kurze Sprech-Abschnitte teilen (satzweise, ~180 Zeichen) — der
// erste Abschnitt wird sofort vertont, der Rest parallel vorbereitet.
function splitForTts(text) {
    const sentences = text.match(/[^.!?…]+[.!?…]*\s*/g) || [text];
    const chunks = [];
    let cur = "";
    for (const s of sentences) {
        if (cur && cur.length + s.length > 180) { chunks.push(cur.trim()); cur = s; }
        else cur += s;
    }
    if (cur.trim()) chunks.push(cur.trim());
    return chunks;
}

    // Jul-2026 (User-Wunsch) — persönliche Kurzbefehle (Phrase → Frage).
    const [shortcuts, setShortcuts] = useState([]);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [scPhrase, setScPhrase] = useState("");
    const [scQuery, setScQuery] = useState("");
    const shortcutsRef = useRef([]);
    useEffect(() => {
        if (!user) return;
        api.get("/assistant/shortcuts").then((r) => {
            shortcutsRef.current = r.data.shortcuts || [];
            setShortcuts(shortcutsRef.current);
        }).catch(() => { /* */ });
    }, [user]);
    const saveShortcuts = async (list) => {
        try {
            const { data } = await api.put("/assistant/shortcuts", { shortcuts: list });
            shortcutsRef.current = data.shortcuts || [];
            setShortcuts(shortcutsRef.current);
        } catch { toast.error("Kurzbefehle konnten nicht gespeichert werden"); }
    };
    const applyShortcuts = (text) => {
        const lc = text.toLowerCase().trim();
        const hit = shortcutsRef.current.find((s) => lc === s.phrase.toLowerCase() || lc.includes(s.phrase.toLowerCase()));
        return hit ? hit.query : text;
    };

    // Jul-2026 (User-Wunsch) — proaktives Start-Briefing (einmal je Sitzung):
    // nennt beim Öffnen die dringendsten To-dos (Zulassungen, Versand, Werkstatt).
    const fetchBriefing = async () => {
        if (!user || !["b2b", "zweiradverkaeufer"].includes(user.role)) return null;
        if (sessionStorage.getItem("tw_briefing_done")) return null;
        sessionStorage.setItem("tw_briefing_done", "1");
        try {
            const { data } = await api.get("/assistant/briefing");
            return (data.text || "").trim() || null;
        } catch { return null; }
    };
    useEffect(() => {
        if (!open) return;
        (async () => {
            const text = await fetchBriefing();
            if (text) setMessages((m) => [...m, { role: "assistant", text }]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Jul-2026 (User-Wunsch) — Sprach-Direktaktionen: Bikevinci FÜHRT Befehle aus
    // (über die bestehenden, abgesicherten APIs) statt nur zu antworten.
    const tryDirectAction = async (text) => {
        const isDealerish = ["b2b", "zweiradverkaeufer"].includes(user?.role || "");
        if (!user || (!isDealerish && user.role !== "admin")) return null;
        const lc = text.toLowerCase();
        const listingMatch = location.pathname.match(/\/listing\/([0-9a-fA-F-]{8,})/);
        try {
            // Offene Leads (Jul-2026, Sprach-Chips): echte Zahlen + heißeste Leads.
            if (/\blead/.test(lc) && /(offen|neu|wie ?viel|zeig|liste|status)/.test(lc)) {
                if (user.role === "admin") {
                    const { data } = await api.get("/admin/leads", { params: { limit: 5 } });
                    const c = data.counts || {};
                    const open = (c.new || 0) + (c.assigned || 0) + (c.in_progress || 0);
                    setCta({ label: "Lead-Übersicht öffnen", to: "/admin/leads" });
                    if (!open) return "Aktuell sind keine offenen Leads da.";
                    return `Es gibt ${open} offene Leads${c.new ? `, davon ${c.new} ganz neu` : ""}. Die Übersicht ist unten verlinkt.`;
                }
                const { data } = await api.get("/dealer/leads");
                const c = data.counts || {};
                const open = (c.assigned || 0) + (c.in_progress || 0);
                setCta({ label: "Meine Leads öffnen", to: "/dealer/leads" });
                if (!open) return "Super, aktuell sind keine offenen Leads zu bearbeiten.";
                const hot = (data.items || []).slice(0, 3)
                    .map((l) => [l.vehicle?.brand, l.vehicle?.model].filter(Boolean).join(" ") || l.subject)
                    .filter(Boolean).join(", ");
                return `Du hast ${open} offene Leads${hot ? `. Ganz oben: ${hot}` : ""}. Die Liste ist unten verlinkt.`;
            }
            if (isDealerish && /versand/.test(lc) && /(offen|wie ?viel|zeig|status|liste|auftr)/.test(lc)) {
                const { data } = await api.get("/dealer/shipping-cockpit");
                const n = data.open?.length || 0;
                setCta({ label: "Versand-Cockpit öffnen", to: "/dealer/versand" });
                if (!n) return "Super, aktuell sind keine Versandaufträge offen.";
                const first = data.open.slice(0, 3).map((r) => r.title).filter(Boolean).join(", ");
                return `Es sind ${n} Versandaufträge offen${first ? `, zum Beispiel ${first}` : ""}. Das Versand-Cockpit ist unten verlinkt.`;
            }
            if (isDealerish && /zulassung/.test(lc) && /(offen|überfällig|ueberfaellig|wie ?viel|zeig|status)/.test(lc)) {
                const { data } = await api.get("/dealer/disposition/open-registrations");
                setCta({ label: "Offene Zulassungen öffnen", to: "/dealer/offene-zulassungen" });
                const n = data.count ?? (data.items || []).length;
                const od = data.overdue || 0;
                if (!n) return "Alles erledigt — es sind keine Zulassungen offen.";
                return `Es sind ${n} Zulassungen offen${od ? `, davon ${od} überfällig` : ""}. Die Liste ist unten verlinkt.`;
            }
            if (isDealerish && listingMatch && /reservierung/.test(lc) && /verläng|verlaeng/.test(lc)) {
                const { data } = await api.post(`/listings/${listingMatch[1]}/dispo/reserve/extend`);
                const until = new Date(data.reserved_until).toLocaleDateString("de-DE");
                return `Erledigt! Die Reservierung wurde um ${data.months} Monat${data.months > 1 ? "e" : ""} bis zum ${until} verlängert. Der Kunde wurde per E-Mail informiert.`;
            }
            if (isDealerish && listingMatch && /(porto|label|briefmarke|internetmarke)/.test(lc) && /(erstell|druck|mach|kauf)/.test(lc)) {
                const { data: cockpit } = await api.get("/dealer/shipping-cockpit");
                const row = [...(cockpit.open || []), ...(cockpit.shipped || [])].find((r) => r.id === listingMatch[1]);
                const c = row?.customer || {};
                if (!c.name || !c.street || !c.postal_code) return "Dafür fehlt mir noch die vollständige Käuferadresse. Bitte ergänze zuerst die Kundendaten in der Dispo.";
                const { data } = await api.post(`/listings/${listingMatch[1]}/dispo/papers-shipment/internetmarke`, {
                    product: "einschreiben_einwurf",
                    recipient: { name: c.name, street: c.street, postal_code: c.postal_code, city: c.city },
                });
                setCta({ label: "Versand-Cockpit öffnen", to: "/dealer/versand" });
                return `Das Porto-Label ist erstellt, Sendungsnummer ${data.tracking_number}. Drucken kannst du es im Versand-Cockpit oder im Reiter Papiere.`;
            }
        } catch (e) {
            const det = e.response?.data?.detail;
            return typeof det === "string" ? `Das hat leider nicht geklappt: ${det}` : "Das hat leider nicht geklappt.";
        }
        return null;
    };

    // Jul-2026 (User-Wunsch) — KI-Arbeitsaktionen (Termin buchen, Rechnung erstellen):
    // zweistufig — Vorschlag (prepare) → Bestätigung per Button oder „ja".
    const pendingActionRef = useRef(null);
    const [consumedActions, setConsumedActions] = useState({});
    const confirmPendingAction = async (actionId) => {
        pendingActionRef.current = null;
        setConsumedActions((c) => ({ ...c, [actionId]: true }));
        try {
            const { data } = await api.post("/assistant/action/confirm", { action_id: actionId });
            const msg = { role: "assistant", text: data.reply };
            if (data.nav_path) msg.nav = { path: data.nav_path, label: data.nav_label || "Öffnen" };
            setMessages((m) => [...m, msg]);
            return data.reply;
        } catch (e) {
            const det = e.response?.data?.detail;
            const txt = typeof det === "string" ? `Das hat nicht geklappt: ${det}` : "Das hat leider nicht geklappt.";
            setMessages((m) => [...m, { role: "assistant", text: txt }]);
            return txt;
        }
    };

    const send = async (textArg, voiceMode = false) => {
        const rawText = (textArg ?? input).trim();
        if (!rawText || sending) return;
        // Jul-2026 (User-Wunsch) — Kurzbefehle: gespeicherte Phrase → volle Frage.
        const text = applyShortcuts(rawText);
        // KI-Arbeitsaktion: offene Bestätigung per „ja"/„nein" (Sprache oder Text).
        if (pendingActionRef.current) {
            const pid = pendingActionRef.current;
            if (/^(ja|jo|jawoll|jawohl|ok(ay)?|passt|mach das|bestätig|ausführen|buchen|erstellen|los)\b/i.test(text)) {
                setMessages((m) => [...m, { role: "user", text: rawText }]);
                setInput("");
                setSending(true);
                const r = await confirmPendingAction(pid);
                setSending(false);
                return r;
            }
            if (/^(nein|nee|abbrechen|abbruch|stopp|halt|nicht|verwerfen)\b/i.test(text)) {
                pendingActionRef.current = null;
                setConsumedActions((c) => ({ ...c, [pid]: true }));
                setMessages((m) => [...m, { role: "user", text: rawText }, { role: "assistant", text: "Alles klar, ich habe die Aktion verworfen." }]);
                setInput("");
                return "Alles klar, ich habe die Aktion verworfen.";
            }
            pendingActionRef.current = null; // andere Frage → Vorschlag verfällt
        }
        // KI-Arbeitsaktion erkennen: Termin buchen / Rechnung erstellen /
        // Aufgabe anlegen / Fahrzeug reservieren / Kunde anlegen (Jul-2026).
        const canAct = user && ["b2b", "zweiradverkaeufer"].includes(user.role);
        const wantsAppointment = /\btermin\w*\b/i.test(text) && /(buch|vereinbar|anleg|erstell|mach|plan|reservier)/i.test(text);
        const wantsInvoice = /\brechnung\w*\b/i.test(text) && /(erstell|anleg|schreib|mach|start)/i.test(text);
        const wantsTask = /\baufgabe\w*\b/i.test(text) && /(anleg|erstell|mach|weise|zuweis|leg)/i.test(text);
        const wantsReserve = /\breservier\w*\b/i.test(text) && !/\btermin\w*\b/i.test(text);
        const wantsCustomer = /\bkund(e|en|in)\w*\b/i.test(text) && /(anleg|erstell|neu|erfass|leg)/i.test(text);
        if (canAct && (wantsAppointment || wantsInvoice || wantsTask || wantsReserve || wantsCustomer)) {
            setMessages((m) => [...m, { role: "user", text: rawText }]);
            setInput("");
            setSending(true);
            try {
                const { data } = await api.post("/assistant/action/prepare", { text });
                const reply = data.ok ? `${data.summary} Soll ich das so ausführen?` : data.message;
                const msg = { role: "assistant", text: reply };
                if (data.ok) { msg.actionId = data.action_id; pendingActionRef.current = data.action_id; }
                setMessages((m) => [...m, msg]);
                return reply;
            } catch {
                const t = "Das hat leider nicht geklappt — bitte versuche es noch einmal.";
                setMessages((m) => [...m, { role: "assistant", text: t }]);
                return t;
            } finally { setSending(false); }
        }
        const nav = user ? matchNavIntent(text) : null;
        if (nav) {
            setMessages((m) => [...m, { role: "user", text: rawText }, { role: "assistant", text: `Alles klar — ich öffne ${nav.label}.` }]);
            setInput("");
            navigate(nav.path);
            return `Ich öffne ${nav.label}.`;
        }
        setCta(detectCta(text, user?.role));
        setSending(true);
        setMessages((m) => [...m, { role: "user", text: rawText }]);
        setInput("");
        try {
            // Jul-2026 (User-Wunsch) — Direktaktionen: ausführen statt nur reden.
            const direct = await tryDirectAction(text);
            if (direct) {
                setMessages((m) => [...m, { role: "assistant", text: direct }]);
                return direct;
            }
            const ctx = detectPageContext(location.pathname);
            const payload = {
                message: text, session_id: sessionId || "new",
                context: ctx?.restrict || undefined,
                page: location.pathname,
                voice: voiceMode || undefined,
            };
            let q = null;
            let spokenUpTo = 0;
            const feedQueue = (full, flushAll) => {
                if (!q) return;
                for (;;) {
                    const mres = full.slice(spokenUpTo).match(/[\s\S]*?[.!?…](?=\s|$)/);
                    if (!mres) break;
                    enqueueSpeech(q, mres[0]);
                    spokenUpTo += mres[0].length;
                }
                if (flushAll) {
                    const tail = full.slice(spokenUpTo).trim();
                    if (tail) { enqueueSpeech(q, tail); spokenUpTo = full.length; }
                }
            };
            let data;
            try {
                // Streaming: Antwort erscheint live, Sprachausgabe beginnt beim 1. Satz.
                if (voiceMode) { q = startQueuedSpeech(); voiceStreamActiveRef.current = true; }
                setMessages((m) => [...m, { role: "assistant", text: "…" }]);
                data = await streamChat(payload, (full) => {
                    const view = parseNavMarker(full).clean;
                    setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: view }; return c; });
                    feedQueue(full, false);
                });
                feedQueue(data.reply, true);
                if (q) q.done = true;
            } catch {
                // Fallback: klassischer (nicht-streamender) Chat-Endpoint.
                if (q) { q.done = true; voiceStreamActiveRef.current = false; }
                const resp = await api.post("/assistant/chat", payload);
                data = resp.data;
            }
            setSessionId(data.session_id);
            if (!user) { try { localStorage.setItem("tw_assistant_guest_session", data.session_id); } catch { /* noop */ } }
            setScope(data.scope || scope);
            const { clean: replyClean, nav: replyNav } = parseNavMarker(data.reply);
            setMessages((m) => {
                const c = [...m];
                const msg = { role: "assistant", text: replyClean, nav: replyNav };
                if (c.length && c[c.length - 1].role === "assistant") c[c.length - 1] = msg;
                else c.push(msg);
                return c;
            });
            if (replyNav?.auto && user) {
                // KI-gesteuerter Seitenwechsel (Jul-2026, User-Wunsch)
                navigate(replyNav.path);
                toast.info(`Bikevinci öffnet: ${replyNav.label}`);
            }
            return replyClean;
        } catch {
            voiceStreamActiveRef.current = false;
            const fallback = "Entschuldigung, ich bin gerade nicht erreichbar.";
            setMessages((m) => {
                const c = [...m];
                if (c.length && c[c.length - 1].role === "assistant" && c[c.length - 1].text === "…") c[c.length - 1] = { role: "assistant", text: fallback };
                else c.push({ role: "assistant", text: fallback });
                return c;
            });
            return fallback;
        } finally { setSending(false); }
    };

    const reset = async () => {
        try { const { data } = await api.post("/assistant/reset"); setSessionId(data.session_id); setMessages([]); setCta(null); }
        catch { toast.error("Neuer Chat fehlgeschlagen"); }
    };

    const onVoice = (text) => { setInput((prev) => (prev ? prev + " " : "") + text); };

    // Sprachausgabe (Jul-2026 Umbau): Antwort wird in Sätze zerlegt — der erste
    // Abschnitt wird SOFORT vertont (OpenAI TTS „alloy"), die restlichen parallel
    // vorbereitet und nahtlos abgespielt. Jederzeit unterbrechbar (Barge-in).
    const speakSessionRef = useRef(0);
    const currentAudioRef = useRef(null);
    const spokenTextRef = useRef("");
    // Jul-2026 (User-Wunsch) — Streaming-Sprachausgabe: Sätze werden schon
    // WÄHREND des Antwort-Streams vertont (spürbar schnellerer Sprechbeginn).
    const voiceStreamActiveRef = useRef(false);
    // Jul-2026 (User-Wunsch) — Whisper-Hören im Freihand-Gespräch.
    const whisperRef = useRef({ active: false });
    const whisperOkRef = useRef(true);
    const startWhisperRef = useRef(null);
    const stopWhisperRef = useRef(() => { });
    const srPausedRef = useRef(false); // Android: SR pausiert, solange die Aufnahme läuft
    const srDeadRef = useRef(false);   // SR meldet not-allowed trotz Mikro-Freigabe (Android)
    // Jul-2026 (Bugfix): Zeitpunkt des Sprech-Endes — die Spracherkennung liefert
    // das Transkript der EIGENEN Ausgabe oft erst 1–2 s später. Ohne Nachhall-
    // Schutz nimmt die KI ihre eigene Antwort als Nutzerfrage (Echo-Schleife).
    const speechEndedAtRef = useRef(0);

    // Gemeinsamer Echo-Abgleich (Jul-2026): Anteil der Wörter, die aus der
    // eigenen (zuletzt gesprochenen) Antwort stammen → eigene Ausgabe erkennen.
    const isOwnEchoText = useCallback((lc) => {
        const spoken = spokenTextRef.current || "";
        if (!spoken) return false;
        const words = lc.split(/\s+/).filter(Boolean);
        if (!words.length) return false;
        const own = words.filter((w) => w.length > 2 && spoken.includes(w)).length;
        return own / words.length > 0.6;
    }, []);

    const stopSpeaking = useCallback(() => {
        speakSessionRef.current += 1;
        try { currentAudioRef.current?.pause(); } catch { /* */ }
        currentAudioRef.current = null;
        try { window.speechSynthesis?.cancel(); } catch { /* */ }
        speechEndedAtRef.current = Date.now();
        speakingRef.current = false;
        setVoiceSpeaking(false);
    }, []);

    const playChunk = useCallback((audioB64, mime, session) => new Promise((resolve) => {
        if (speakSessionRef.current !== session) { resolve(); return; }
        const audio = new Audio(`data:${mime || "audio/mp3"};base64,${audioB64}`);
        currentAudioRef.current = audio;
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
    }), []);

    const fallbackSpeak = useCallback((t, session) => new Promise((resolve) => {
        try {
            const synth = window.speechSynthesis;
            if (!synth || speakSessionRef.current !== session) { resolve(); return; }
            synth.cancel();
            const u = new SpeechSynthesisUtterance(t);
            u.lang = "de-DE";
            const de = (synth.getVoices() || []).find((v) => v.lang && v.lang.toLowerCase().startsWith("de"));
            if (de) u.voice = de;
            u.onend = resolve; u.onerror = resolve;
            synth.speak(u);
        } catch { resolve(); }
    }), []);

    const speak = useCallback(async (text) => {
        const clean = sanitizeForSpeech(text);
        if (!clean) return;
        stopSpeaking();
        const session = speakSessionRef.current;
        speakingRef.current = true;
        setVoiceSpeaking(true);
        spokenTextRef.current = clean.toLowerCase();
        const chunks = splitForTts(clean);
        // Alle TTS-Anfragen parallel starten — Wiedergabe strikt in Reihenfolge.
        const requests = chunks.map((c) =>
            api.post("/assistant/tts", { text: c, voice: getTtsVoice() }).then((r) => r.data).catch(() => null));
        let usedFallback = false;
        for (let idx = 0; idx < requests.length; idx++) {
            if (speakSessionRef.current !== session) return;
            const data = await requests[idx];
            if (speakSessionRef.current !== session) return;
            if (data?.audio_base64) {
                await playChunk(data.audio_base64, data.mime, session);
            } else if (!usedFallback) {
                usedFallback = true;
                await fallbackSpeak(chunks.slice(idx).join(" "), session);
                break;
            }
        }
        if (speakSessionRef.current === session) {
            // spokenTextRef bewusst NICHT leeren — Nachhall-Echo-Vergleich.
            speechEndedAtRef.current = Date.now();
            speakingRef.current = false;
            setVoiceSpeaking(false);
        }
    }, [stopSpeaking, playChunk, fallbackSpeak]);

    // Jul-2026 (User-Wunsch) — Speech-Queue fürs Streaming: Sätze werden einzeln
    // eingereiht und abgespielt, WÄHREND die Antwort noch generiert wird.
    const startQueuedSpeech = useCallback(() => {
        stopSpeaking();
        const session = speakSessionRef.current;
        speakingRef.current = true;
        setVoiceSpeaking(true);
        spokenTextRef.current = "";
        const q = { pending: [], done: false };
        (async () => {
            while (speakSessionRef.current === session && (!q.done || q.pending.length)) {
                const item = q.pending.shift();
                if (!item) { await new Promise((r) => setTimeout(r, 120)); continue; }
                const data = await item.req;
                if (speakSessionRef.current !== session) return;
                if (data?.audio_base64) await playChunk(data.audio_base64, data.mime, session);
                else await fallbackSpeak(item.text, session);
            }
            if (speakSessionRef.current === session) {
                speechEndedAtRef.current = Date.now();
                speakingRef.current = false;
                setVoiceSpeaking(false);
            }
        })();
        return q;
    }, [stopSpeaking, playChunk, fallbackSpeak]);

    const enqueueSpeech = useCallback((q, text) => {
        const clean = sanitizeForSpeech(text);
        if (!clean) return;
        spokenTextRef.current = `${spokenTextRef.current} ${clean.toLowerCase()}`.slice(-2000);
        q.pending.push({
            text: clean,
            req: api.post("/assistant/tts", { text: clean, voice: getTtsVoice() }).then((r) => r.data).catch(() => null),
        });
    }, []);

    const endConvo = useCallback(() => {
        if (convoTimerRef.current) clearTimeout(convoTimerRef.current);
        modeRef.current = "wake"; setVoiceListening(false);
        stopWhisperRef.current();
        if (srPausedRef.current) {
            srPausedRef.current = false;
            try { recognitionRef.current?.start(); } catch { /* */ }
        }
    }, []);

    // Freihand-Modus (Jun-2026, User-Wunsch): nach der Antwort automatisch weiter
    // zuhören — echtes Gespräch ohne erneutes Wake-Word. Nach 45 s Stille zurück
    // in den Wake-Modus, damit Hintergrundgespräche nicht als Fragen gesendet werden.
    const armConvoTimeout = useCallback(() => {
        if (convoTimerRef.current) clearTimeout(convoTimerRef.current);
        convoTimerRef.current = setTimeout(() => {
            if (modeRef.current === "convo" && !speakingRef.current) {
                modeRef.current = "wake";
                setVoiceListening(false);
                stopWhisperRef.current();
                if (srPausedRef.current) {
                    srPausedRef.current = false;
                    try { recognitionRef.current?.start(); } catch { /* */ }
                }
                toast("Gespräch pausiert (keine Frage gehört). Sage das Aktivierungswort oder tippe „Gespräch starten“.");
            }
        }, 45000);
    }, []);

    const enterConvo = useCallback(() => {
        modeRef.current = "convo";
        setVoiceListening(true);
        armConvoTimeout();
        // Android: Wake-Word-Erkennung pausieren, damit die Aufnahme (Whisper)
        // das exklusive Mikrofon übernehmen kann.
        if (IS_ANDROID && recognitionRef.current) {
            srPausedRef.current = true;
            try { recognitionRef.current.stop(); } catch { /* */ }
        }
        // Bugfix (Jul-2026): Aufnahme-Schleife beim Gesprächseintritt STARTEN
        // (fehlte bisher — das Freihand-Hören begann sonst nie).
        setTimeout(() => startWhisperRef.current?.(), IS_ANDROID ? 400 : 0);
    }, [armConvoTimeout]);

    // Eine Sprach-„Runde": Frage senden → Antwort sprechen → weiter zuhören.
    const processQuery = useCallback(async (text) => {
        let t = (text || "").trim();
        // Wake-Word aus der Frage entfernen („Bikevinci, wie viele…" → „wie viele…")
        const stripped = wakeStrip(t).replace(/^[,.\s!?]+/, "").trim();
        if (stripped.length > 2) t = stripped;
        else if (!stripped && wakeTest(t.toLowerCase())) { enterConvo(); return; }
        if (!t) return;
        // Selbst-Hören-Schutz (Jul-2026): eigene Ausgabe nie als Frage verarbeiten.
        if (isOwnEchoText(t.toLowerCase()) && (Date.now() - speechEndedAtRef.current) < 4000) {
            if (modeRef.current === "convo") armConvoTimeout();
            return;
        }
        if (/\b(stopp?|tschüss|tschuss|aufhören|aufhoeren|beenden|ende)\b/i.test(t) && t.split(/\s+/).length <= 3) {
            endConvo(); await speak("Alles klar, bis später!"); return;
        }
        if (convoTimerRef.current) clearTimeout(convoTimerRef.current);
        modeRef.current = "busy";
        setVoiceListening(false);
        const reply = await (sendRef.current ? sendRef.current(t, true) : Promise.resolve(""));
        if (voiceStreamActiveRef.current) {
            // Streaming-Sprachausgabe läuft bereits — nur auf ihr Ende warten.
            voiceStreamActiveRef.current = false;
            while (speakingRef.current) { await new Promise((r) => setTimeout(r, 150)); }
        } else {
            await speak(reply);
        }
        if (voiceWakeRef.current) enterConvo(); // Freihand: direkt weiter zuhören (kein Wake-Word nötig)
        else endConvo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [speak, endConvo, enterConvo]);

    // Jul-2026 (User-Wunsch) — „Besseres Hören": Im Freihand-Gespräch wird die
    // Frage per MediaRecorder aufgenommen (Stille-Erkennung) und mit Whisper
    // transkribiert — deutlich robuster bei Fachbegriffen. Fallback: Browser-SR.
    const stopWhisper = useCallback(() => {
        const w = whisperRef.current;
        w.active = false;
        if (w.loop) clearInterval(w.loop);
        try { if (w.rec && w.rec.state !== "inactive") { w.rec.onstop = null; w.rec.stop(); } } catch { /* */ }
        try { w.stream?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
        try { w.ctx?.close(); } catch { /* */ }
        whisperRef.current = { active: false };
    }, []);
    stopWhisperRef.current = stopWhisper;

    const startWhisper = useCallback(async () => {
        if (!whisperOkRef.current || !user || whisperRef.current.active || modeRef.current !== "convo") return;
        // Sep-2026 (User-Wunsch, Latenz-Fix): Ist die lokale Echtzeit-Erkennung
        // (Web Speech API, Chrome/Edge) verfügbar UND nicht gedrosselt, übernimmt
        // SIE die Frage-Erkennung praktisch latenzfrei. Dann wird der langsame
        // Whisper-Recorder (Aufnahme→Upload→Whisper, ~3–5 s) gar nicht gestartet.
        // Auf Android meldet SR oft „not-allowed" (srDead) → dann greift Whisper.
        const _SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (_SR && !srDeadRef.current) { whisperOkRef.current = false; return; }
        if (!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder)) { whisperOkRef.current = false; return; }
        let stream;
        // Echounterdrückung (Jul-2026, User-Wunsch): Browser filtert die eigene
        // Sprachausgabe aus dem Mikrofonsignal — wichtig für Barge-in ohne Kopfhörer.
        const AUDIO_OPTS = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } };
        try { stream = await navigator.mediaDevices.getUserMedia(AUDIO_OPTS); }
        catch {
            // Android: Mikro evtl. noch von der Spracherkennung belegt — kurz warten, 1× erneut.
            await new Promise((r) => setTimeout(r, 500));
            if (modeRef.current !== "convo") return;
            try { stream = await navigator.mediaDevices.getUserMedia(AUDIO_OPTS); }
            catch { whisperOkRef.current = false; return; }
        }
        let ctx;
        try { ctx = new (window.AudioContext || window.webkitAudioContext)(); }
        catch { stream.getTracks().forEach((t) => t.stop()); whisperOkRef.current = false; return; }
        try { await ctx.resume(); } catch { /* */ }
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
        const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
        const chunks = [];
        rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
        const w = { active: true, stream, ctx, rec, loop: null };
        whisperRef.current = w;
        try { rec.start(250); } catch { stopWhisper(); whisperOkRef.current = false; return; }
        const buf = new Uint8Array(analyser.fftSize);
        let hadSpeech = false, silenceMs = 0, totalMs = 0;
        let bargeTicks = 0, wasSpeaking = false;
        const TICK = 120;
        w.loop = setInterval(() => {
            if (!w.active) { clearInterval(w.loop); return; }
            if (modeRef.current !== "convo") { stopWhisper(); return; }
            if (speakingRef.current) {
                // Barge-in (Jul-2026, User-Wunsch): Während die KI spricht, misst das
                // Mikrofon weiter (mit Echounterdrückung). Spricht der Nutzer deutlich
                // dazwischen (~360 ms), stoppt die Sprachausgabe SOFORT und die Frage
                // wird ganz normal aufgenommen.
                wasSpeaking = true;
                analyser.getByteTimeDomainData(buf);
                let s2 = 0;
                for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; s2 += v * v; }
                const rmsSpk = Math.sqrt(s2 / buf.length);
                bargeTicks = rmsSpk > 0.05 ? bargeTicks + 1 : 0;
                if (bargeTicks >= 3) {
                    stopSpeaking();
                    bargeTicks = 0;
                    hadSpeech = true; silenceMs = 0; totalMs = 0;
                } else {
                    hadSpeech = false; silenceMs = 0; totalMs = 0;
                }
                return;
            }
            if (wasSpeaking && !hadSpeech) {
                // Sprechende ohne Barge-in → Aufnahme frisch starten, damit der
                // Puffer die eigene Ausgabe/Nachhall NICHT enthält (kein Selbst-Hören).
                stopWhisper();
                setTimeout(() => startWhisperRef.current?.(), 250);
                return;
            }
            wasSpeaking = false;
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
            const rms = Math.sqrt(sum / buf.length);
            totalMs += TICK;
            if (rms > 0.025) { hadSpeech = true; silenceMs = 0; armConvoTimeout(); }
            else if (hadSpeech) silenceMs += TICK;
            if ((hadSpeech && silenceMs >= 800) || totalMs >= 20000) {
                clearInterval(w.loop);
                w.active = false;
                rec.onstop = async () => {
                    try { stream.getTracks().forEach((t) => t.stop()); ctx.close(); } catch { /* */ }
                    whisperRef.current = { active: false };
                    const blob = new Blob(chunks, { type: mime || "audio/webm" });
                    if (hadSpeech && blob.size > 4000 && modeRef.current === "convo") {
                        try {
                            const fd = new FormData();
                            fd.append("file", blob, "frage.webm");
                            const { data } = await api.post("/assistant/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
                            const t = (data?.text || "").trim();
                            // Selbst-Hören-Schutz (Jul-2026): Transkripte, die überwiegend
                            // aus der eigenen Sprachausgabe bestehen, verwerfen.
                            const echo = t && isOwnEchoText(t.toLowerCase())
                                && (speakingRef.current || (Date.now() - speechEndedAtRef.current) < 4000);
                            if (t && !echo && processRef.current) { processRef.current(t); return; }
                        } catch { /* Transkription fehlgeschlagen → nächste Runde */ }
                    }
                    if (modeRef.current === "convo") startWhisperRef.current?.();
                };
                try { rec.stop(); } catch { /* */ }
            }
        }, TICK);
    }, [user, stopWhisper, armConvoTimeout]);
    startWhisperRef.current = startWhisper;

    // Prüft, ob der Mikrofon-Zugriff tatsächlich möglich ist (verhindert „aktiv",
    // obwohl der Browser/iframe das Mikrofon blockiert).
    const ensureMicAccess = useCallback(async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            return { ok: false, reason: window.isSecureContext ? "unsupported" : "insecure" };
        }
        try {
            const s = await navigator.mediaDevices.getUserMedia({ audio: true });
            s.getTracks().forEach((t) => t.stop());
            return { ok: true };
        } catch (err) {
            const name = err?.name || "";
            const msg = (err?.message || "").toLowerCase();
            if (name === "NotAllowedError" || name === "SecurityError") {
                // Im eingebetteten iframe scheitert getUserMedia per Permissions-Policy
                // selbst bei erteilter Browser-Berechtigung → „blocked" (neuer Tab).
                const policyBlock = msg.includes("permissions policy") || msg.includes("permission policy") || msg.includes("disallowed");
                return { ok: false, reason: (policyBlock || isEmbedded()) ? "blocked" : "denied" };
            }
            if (name === "NotFoundError" || name === "DevicesNotFoundError") return { ok: false, reason: "nodevice" };
            return { ok: false, reason: "error" };
        }
    }, []);

    // Gemeinsame Prüfung für Wake-Word UND Freihand-Start: Browser-Support + Mikrofon.
    const ensureVoiceReady = useCallback(async () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            setMicState("unsupported"); setShowMicHelp(true);
            toast.error("Sprachaktivierung wird von diesem Browser nicht unterstützt (Chrome/Edge empfohlen).");
            return false;
        }
        const res = await ensureMicAccess();
        if (!res.ok) {
            setMicState(res.reason);
            setShowMicHelp(true);
            if (res.reason === "blocked") {
                toast.error("Mikrofon ist in dieser eingebetteten Vorschau blockiert. Bitte „In neuem Tab öffnen“ und dort erlauben.", { duration: 9000 });
            } else if (res.reason === "denied") {
                toast.error("Mikrofon-Zugriff verweigert. Bitte im Browser erlauben (Schloss-Symbol → Mikrofon → Zulassen) und erneut versuchen.", { duration: 9000 });
            } else if (res.reason === "nodevice") {
                toast.error("Kein Mikrofon gefunden. Bitte ein Mikrofon anschließen.");
            } else if (res.reason === "insecure") {
                toast.error("Mikrofon benötigt eine sichere (HTTPS-)Verbindung.");
            } else {
                toast.error("Mikrofon nicht verfügbar.");
            }
            return false;
        }
        setMicState("granted");
        setShowMicHelp(false);
        return true;
    }, [ensureMicAccess]);

    const toggleVoiceWake = useCallback(async () => {
        if (voiceWake) {
            setVoiceWake(false);
            try { localStorage.setItem("tw_bikevinci_wake", "0"); } catch { /* */ }
            toast("Sprachaktivierung aus.");
            return;
        }
        if (!(await ensureVoiceReady())) return; // NICHT aktivieren, solange das Mikrofon nicht erreichbar ist
        setVoiceWake(true);
        try { localStorage.setItem("tw_bikevinci_wake", "1"); } catch { /* */ }
        toast.success(`Sprachaktivierung an: Sage „${wakeWord}“ oder „Bikevinci“, um mit der KI zu sprechen.`);
    }, [voiceWake, ensureVoiceReady, wakeWord]);

    // Freihand-Gespräch direkt starten — OHNE Wake-Word (Jun-2026, User-Wunsch).
    const startHandsFree = useCallback(async () => {
        if (voiceWake) { enterConvo(); return; }
        if (!(await ensureVoiceReady())) return;
        startInConvoRef.current = true;
        setVoiceWake(true);
        try { localStorage.setItem("tw_bikevinci_wake", "1"); } catch { /* */ }
        toast.success("Freihand-Gespräch gestartet — ich höre zu, stell einfach deine Frage.");
    }, [voiceWake, enterConvo, ensureVoiceReady]);

    // Aktuelle Closures in Refs spiegeln (für die stabilen Recognition-Callbacks).
    voiceWakeRef.current = voiceWake;
    sendRef.current = send;
    openRef.current = () => setOpen(true);
    processRef.current = processQuery;

    // Kontinuierliche Wake-Word-Erkennung (Web Speech API). Nur Chrome/Edge.
    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!voiceWake) {
            if (recognitionRef.current) { try { recognitionRef.current.onend = null; recognitionRef.current.stop(); } catch { /* */ } recognitionRef.current = null; }
            modeRef.current = "wake"; setVoiceListening(false);
            stopSpeaking();
            return;
        }
        if (!SR) {
            toast.error("Sprachaktivierung wird von diesem Browser nicht unterstützt (Chrome/Edge empfohlen).");
            setVoiceWake(false); return;
        }
        const rec = new SR();
        // Jul-2026: interimResults=true → Wake-Word wird schon aus Zwischen-
        // ergebnissen erkannt (deutlich reaktiver) + Barge-in beim Vorlesen.
        rec.lang = "de-DE"; rec.continuous = true; rec.interimResults = true;
        rec.onresult = (event) => {
            let interim = "", finals = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const tr = event.results[i][0].transcript;
                if (event.results[i].isFinal) finals += tr;
                else interim += tr;
            }
            const finalChunk = finals.trim();
            const anyChunk = `${finals} ${interim}`.trim();
            if (!anyChunk) return;
            const lcAll = anyChunk.toLowerCase();
            // Echo-Erkennung: Anteil der Wörter, die aus der eigenen (zuletzt
            // gesprochenen) Antwort stammen.
            const isOwnEcho = isOwnEchoText;
            // Jul-2026 (Bugfix): Nachhall-Schutz — bis 3 s NACH dem Sprechende
            // eintreffende Transkripte, die überwiegend aus der eigenen Ausgabe
            // bestehen, verwerfen (sonst beantwortet die KI ihre eigene Antwort).
            if (!speakingRef.current && (Date.now() - speechEndedAtRef.current) < 3000 && isOwnEcho(lcAll)) {
                if (modeRef.current === "convo") armConvoTimeout();
                return;
            }
            // Barge-in (Jul-2026, User-Wunsch): Während die KI vorliest, hört sie
            // weiter zu. Stopp-Wort, Wake-Word oder ein echter Einwurf (≥3 Wörter,
            // die nicht aus der eigenen Sprachausgabe stammen) unterbricht sofort.
            if (speakingRef.current) {
                const words = lcAll.split(/\s+/).filter(Boolean);
                const isEcho = isOwnEcho(lcAll);
                const stopWord = /\b(stopp?|halt|warte|moment|leise)\b/.test(lcAll);
                const wake = wakeTest(lcAll);
                if (stopWord || wake || (!isEcho && finalChunk && words.length >= 3)) {
                    stopSpeaking();
                    const rest = wakeStrip(finalChunk).replace(/\b(stopp?|halt|warte|moment|leise)\b/gi, "").replace(/^[,.\s!?]+/, "").trim();
                    if (rest.length > 2) { processRef.current && processRef.current(rest); }
                    else { enterConvo(); }
                }
                return;
            }
            if (modeRef.current === "wake") {
                if (wakeTest(lcAll)) {
                    openRef.current && openRef.current();
                    if (finalChunk) {
                        const remainder = wakeStrip(finalChunk).replace(/^[,.\s!?]+/, "").trim();
                        if (remainder.length > 2) { processRef.current && processRef.current(remainder); return; }
                    }
                    enterConvo(); // schon beim Zwischenergebnis reagieren — Frage folgt als Final
                }
            } else if (modeRef.current === "convo") {
                // Sep-2026: Läuft SR (Chrome/Edge, nicht gedrosselt), erkennt SIE die
                // Frage direkt — praktisch latenzfrei. Nur wenn SR gedrosselt ist
                // (srDead, z. B. Android) übernimmt der Whisper-Recorder.
                if (user && whisperOkRef.current && srDeadRef.current) { armConvoTimeout(); return; }
                if (finalChunk) { processRef.current && processRef.current(finalChunk); }
                else { armConvoTimeout(); } // Nutzer spricht noch → Pause-Timeout verlängern
            }
        };
        rec.onerror = (e) => {
            if (e.error === "not-allowed" || e.error === "service-not-allowed") {
                if (srPausedRef.current) return; // absichtlich pausiert (Android-Gespräch)
                (async () => {
                // Erst ECHTEN Mikrofon-Zugriff prüfen: Auf Android meldet die
                // Spracherkennung oft „not-allowed", obwohl das Mikro erlaubt ist
                // (Dienst gedrosselt / Mikro belegt) — dann NICHT deaktivieren.
                const probe = await ensureMicAccess();
                if (probe.ok) {
                    srDeadRef.current = true;
                    setMicState("granted");
                    if (modeRef.current === "wake") {
                        toast("Das Aktivierungswort ist auf diesem Gerät gerade nicht verfügbar. Tippe im Assistenten auf „Gespräch starten“ – ich höre dann direkt zu.", { duration: 9000 });
                    }
                    return;
                }
                // Mikrofon im Browser erlaubt, aber iframe blockiert die Spracherkennung
                // → „blocked" (neuer Tab), sonst echte Verweigerung („denied").
                const reason = isEmbedded() ? "blocked" : "denied";
                setMicState(reason); setShowMicHelp(true);
                toast.error(reason === "blocked"
                    ? "Spracherkennung in der eingebetteten Vorschau blockiert. Bitte „In neuem Tab öffnen“."
                    : "Mikrofon-Zugriff für die Sprachaktivierung verweigert. Bitte im Browser erlauben.",
                    { duration: 9000 });
                setVoiceWake(false);
                try { localStorage.setItem("tw_bikevinci_wake", "0"); } catch { /* */ }
                })();
            }
        };
        rec.onend = () => {
            if (!voiceWakeRef.current || srPausedRef.current || srDeadRef.current) return;
            // Verzögerter Neustart (300 ms) — verhindert Androids „not-allowed"-
            // Drosselung bei zu schnellen Restart-Schleifen.
            setTimeout(() => {
                if (voiceWakeRef.current && !srPausedRef.current && !srDeadRef.current) {
                    try { rec.start(); } catch { /* bereits gestartet */ }
                }
            }, 300);
        };
        srDeadRef.current = false;
        try { rec.start(); } catch { /* */ }
        recognitionRef.current = rec;
        if (startInConvoRef.current) {
            startInConvoRef.current = false;
            enterConvo(); // Freihand-Start: sofort zuhören, kein Wake-Word nötig
        } else {
            modeRef.current = "wake";
        }
        return () => { try { rec.onend = null; rec.stop(); } catch { /* */ } recognitionRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voiceWake, enterConvo, stopSpeaking, armConvoTimeout]);

    // Jul-2026 (Bugfix, User-Meldung) — Transkriptions-Aufnahme (VoiceRecordButton)
    // und Wake-Word-Erkennung teilen sich das EXKLUSIVE Mikrofon. Vor der Aufnahme
    // pausiert der Button die Erkennung per Event, danach läuft sie weiter —
    // sonst erschien fälschlich „Mikrofon nicht aktiviert", obwohl es aktiv war.
    useEffect(() => {
        const pause = () => {
            if (!recognitionRef.current) return;
            srPausedRef.current = true;
            try { recognitionRef.current.stop(); } catch { /* */ }
        };
        const resume = () => {
            if (!srPausedRef.current) return;
            srPausedRef.current = false;
            if (voiceWakeRef.current && !srDeadRef.current) {
                setTimeout(() => { try { recognitionRef.current?.start(); } catch { /* */ } }, 300);
            }
        };
        window.addEventListener("tw-wake-sr-pause", pause);
        window.addEventListener("tw-wake-sr-resume", resume);
        return () => {
            window.removeEventListener("tw-wake-sr-pause", pause);
            window.removeEventListener("tw-wake-sr-resume", resume);
        };
    }, []);

    // Leichtgewichtiges Nutzungs-Tracking (best-effort, blockiert die UI nie).
    const track = useCallback((event_type, label) => {
        if (!user) return; // Gäste: kein Tracking (Endpoint erfordert Login)
        api.post("/assistant/track", { event_type, label: label || "", page: location.pathname }).catch(() => { /* noop */ });
    }, [location.pathname, user]);

    const pickSuggestion = (s) => {
        track("chip_click", s.text);
        send(s.text);
        // Chip-CTA ist maßgeblich (überschreibt die Schlüsselwort-Erkennung aus send).
        setCta(s.to ? { label: s.ctaLabel, to: s.to } : null);
    };

    const goCta = () => {
        if (!cta?.to) return;
        track("cta_click", cta.label);
        setOpen(false);
        navigate(cta.to);
    };

    const suggestions = SUGGESTIONS[user?.role] || SUGGESTIONS._default;

    // Jul-2026 (User-Wunsch) — Sprach-Schnellbefehle: antippbare Chips (v. a. fürs
    // Handy), die die Frage sofort stellen und die Antwort VORLESEN.
    const VOICE_CHIP_SETS = {
        b2b: [
            { label: "Offene Leads", query: "Zeig mir die offenen Leads" },
            { label: "Was ist zu tun?", query: "Was ist heute zu tun? Fasse kurz zusammen." },
            { label: "Aktive Fahrzeuge", query: "Wie viele aktive Fahrzeuge haben wir gerade?" },
            { label: "Offener Versand", query: "Wie viele Versandaufträge sind offen?" },
            { label: "Offene Zulassungen", query: "Wie viele Zulassungen sind offen?" },
        ],
        admin: [
            { label: "Offene Leads", query: "Zeig mir die offenen Leads" },
            { label: "Plattform-Überblick", query: "Gib mir einen kurzen Überblick über den Plattform-Status." },
            { label: "Neue Händler", query: "Gibt es neue Händler-Registrierungen zur Freigabe?" },
        ],
    };
    VOICE_CHIP_SETS.zweiradverkaeufer = VOICE_CHIP_SETS.b2b;
    const voiceChips = user ? (VOICE_CHIP_SETS[user.role] || []) : [];
    const runVoiceChip = (c) => {
        if (sending) return;
        stopSpeaking();
        track("voice_chip", c.label);
        processRef.current && processRef.current(c.query);
    };

    // Proaktive, seitenbezogene Vorschläge zuerst (dedupliziert mit den Rollen-Chips).
    const pageCtx = detectPageContext(location.pathname);
    const mergedSuggestions = (() => {
        if (!pageCtx) return suggestions;
        const seen = new Set();
        const out = [];
        for (const s of [...pageCtx.suggestions, ...suggestions]) {
            if (seen.has(s.text)) continue;
            seen.add(s.text);
            out.push(s);
            if (out.length >= 5) break;
        }
        return out;
    })();

    return (
        <>
            {!open && (
                <>
                    {nudge && pageCtx && (
                        <div style={fabPos ? { right: fabPos.r, bottom: fabPos.b + 64 } : undefined} className="fixed bottom-44 md:bottom-40 right-4 z-[100] w-64 bg-white rounded-sm shadow-2xl border border-gray-200 p-3 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="chatbot-nudge">
                            <button onClick={dismissNudge} className="absolute top-1.5 right-1.5 p-0.5 text-gray-400 hover:text-gray-700" title="Nicht mehr anzeigen" data-testid="chatbot-nudge-dismiss"><X className="w-3.5 h-3.5" /></button>
                            <div className="flex items-start gap-2">
                                <div className="h-7 w-7 rounded-full bg-[#111827] text-[#FFCC00] flex items-center justify-center shrink-0"><Bot className="w-4 h-4" /></div>
                                <p className="text-xs text-gray-700 font-medium pr-3 leading-snug">{pageCtx.nudge}</p>
                            </div>
                            <button
                                onClick={() => { track("nudge_open", pageCtx.nudge); setNudge(false); setOpen(true); }}
                                className="mt-2 w-full h-8 rounded-sm bg-[#FFCC00] hover:bg-yellow-300 text-[#111827] text-xs font-bold transition-colors"
                                data-testid="chatbot-nudge-open"
                            >
                                Ja, gerne
                            </button>
                        </div>
                    )}
                    <button
                        onClick={() => { if (Date.now() - lastDragRef.current < 300) return; setNudge(false); setOpen(true); }}
                        onPointerDown={onFabPointerDown}
                        style={fabStyle}
                        className={`fixed bottom-28 md:bottom-24 right-4 z-[100] h-14 rounded-full bg-[#111827] hover:bg-black text-[#FFCC00] shadow-xl flex items-center gap-2 pl-4 pr-5 transition-transform hover:scale-105 ${voiceWake ? "ring-4 ring-[#FFCC00]/60 animate-pulse" : ""}`}
                        title={voiceWake ? `KI-Assistent · Sprachaktivierung „${wakeWord}“ aktiv · zum Verschieben ziehen` : "KI-Assistent von TwoWheels — zum Verschieben ziehen"}
                        data-testid="chatbot-fab"
                    >
                        <Bot className="w-6 h-6 shrink-0" />
                        <span className="font-bold text-sm text-white whitespace-nowrap">KI-Assistent</span>
                    </button>
                </>
            )}

            {open && (
                <div style={panelStyle} className="fixed bottom-28 md:bottom-24 right-4 z-[100] w-[calc(100vw-2rem)] sm:w-96 h-[70vh] sm:h-[560px] bg-white rounded-sm shadow-2xl border border-gray-200 flex flex-col overflow-hidden" data-testid="chatbot-panel">
                    <div className="flex items-center gap-2 px-4 py-3 bg-[#111827] text-white shrink-0">
                        <Bot className="w-5 h-5 text-[#FFCC00]" />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm leading-tight">KI-Assistent</div>
                            {scope && <div className="text-[10px] text-gray-300 truncate" data-testid="chatbot-scope">{scope}</div>}
                        </div>
                        <button
                            onClick={toggleTranscribe}
                            className={`p-1 ${showTranscribe ? "text-[#FFCC00]" : "text-gray-300 hover:text-white"}`}
                            title={showTranscribe ? "Sprachaufnahme (Transkription) ausblenden" : "Sprachaufnahme (Transkription) einblenden"}
                            data-testid="chatbot-transcribe-toggle"
                        >
                            {showTranscribe ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={() => setShowShortcuts((v) => !v)}
                            className={`p-1 ${showShortcuts ? "text-[#FFCC00]" : "text-gray-300 hover:text-white"}`}
                            title="Kurzbefehle verwalten"
                            data-testid="chatbot-shortcuts-toggle"
                        >
                            <Zap className="w-4 h-4" />
                        </button>
                        <button onClick={reset} className="p-1 text-gray-300 hover:text-white" title="Neuer Chat" data-testid="chatbot-reset"><RotateCcw className="w-4 h-4" /></button>
                        <button
                            onClick={toggleVoiceWake}
                            className={`p-1 ${voiceWake ? "text-[#FFCC00]" : "text-gray-300 hover:text-white"}`}
                            title={voiceWake ? `Sprachaktivierung „${wakeWord}“ an — zum Ausschalten klicken` : `Sprachaktivierung „${wakeWord}“ einschalten`}
                            data-testid="chatbot-wake-toggle"
                        >
                            <Radio className="w-4 h-4" />
                        </button>
                        <button onClick={() => setOpen(false)} className="p-1 text-gray-300 hover:text-white" data-testid="chatbot-close"><X className="w-5 h-5" /></button>
                    </div>

                    {showShortcuts && (
                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 max-h-44 overflow-y-auto shrink-0" data-testid="chatbot-shortcuts-panel">
                            <div className="text-[11px] font-bold text-gray-700 mb-1">Kurzbefehle (Phrase → Frage/Befehl)</div>
                            {shortcuts.length === 0 && <div className="text-[10px] text-gray-500 mb-1">Noch keine Kurzbefehle — z.B. „mein Tagesbericht“ → „Wie laufen meine Inserate und was ist heute zu tun?“</div>}
                            {shortcuts.map((s, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-[11px] py-0.5">
                                    <span className="font-bold text-[#111827] shrink-0">„{s.phrase}“</span>
                                    <span className="text-gray-500 truncate flex-1">→ {s.query}</span>
                                    <button onClick={() => saveShortcuts(shortcuts.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600 shrink-0" data-testid={`chatbot-shortcut-del-${i}`}><Trash2 className="w-3 h-3" /></button>
                                </div>
                            ))}
                            <div className="flex items-center gap-1 mt-1.5">
                                <input value={scPhrase} onChange={(e) => setScPhrase(e.target.value)} placeholder="Phrase" className="flex-1 min-w-0 text-[11px] border border-gray-300 rounded-sm px-1.5 py-1" data-testid="chatbot-shortcut-phrase" />
                                <input value={scQuery} onChange={(e) => setScQuery(e.target.value)} placeholder="Frage/Befehl" className="flex-1 min-w-0 text-[11px] border border-gray-300 rounded-sm px-1.5 py-1" data-testid="chatbot-shortcut-query" />
                                <button
                                    onClick={() => { if (scPhrase.trim() && scQuery.trim()) { saveShortcuts([...shortcuts, { phrase: scPhrase.trim(), query: scQuery.trim() }]); setScPhrase(""); setScQuery(""); } }}
                                    className="h-6 w-6 flex items-center justify-center rounded-sm bg-[#111827] text-white hover:bg-black shrink-0"
                                    data-testid="chatbot-shortcut-add"
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    )}

                    {showMicHelp && (
                        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200" data-testid="chatbot-mic-help">
                            <div className="flex items-start gap-2">
                                <MicOff className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold text-gray-800">
                                        {micState === "blocked" ? "Mikrofon in der Vorschau blockiert"
                                            : micState === "unsupported" ? "Browser unterstützt keine Sprachaktivierung"
                                                : "Mikrofon-Zugriff nötig"}
                                    </p>
                                    <p className="text-[11px] text-gray-600 leading-snug mt-0.5">
                                        {micState === "blocked"
                                            ? "Die eingebettete Vorschau lässt die freihändige Spracherkennung („Bikevinci“) nicht zu – das ist eine Browser-/iframe-Beschränkung, nicht deine Einstellung. Öffne die App in einem eigenen Tab. Tipp: Das Mikrofon-Symbol unten (Aufnahme & Senden) funktioniert auch hier."
                                            : micState === "unsupported"
                                                ? "Bitte Chrome oder Edge (Desktop) verwenden, um „Bikevinci“ per Sprache zu nutzen."
                                                : "Bitte erlaube den Mikrofon-Zugriff: Schloss-/Kamera-Symbol in der Adressleiste → Mikrofon → Zulassen, dann erneut aktivieren."}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <a
                                            href={window.location.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#111827] bg-[#FFCC00] hover:bg-yellow-300 rounded-sm px-2 py-1 transition-colors"
                                            data-testid="chatbot-mic-newtab"
                                        >
                                            In neuem Tab öffnen <ExternalLink className="w-3 h-3" />
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => setShowMicHelp(false)}
                                            className="text-[11px] font-semibold text-gray-500 hover:text-gray-800"
                                            data-testid="chatbot-mic-help-close"
                                        >
                                            Schließen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50" data-testid="chatbot-messages">
                        <div className="flex items-start gap-1.5 text-[10px] text-gray-500 bg-white border border-gray-200 rounded-sm px-2 py-1.5" data-testid="chatbot-ai-disclosure">
                            <Bot className="w-3 h-3 mt-0.5 shrink-0 text-gray-400" />
                            <span>Sie chatten mit einem KI-Assistenten. Antworten werden automatisiert erzeugt und können Fehler enthalten – bitte wichtige Angaben prüfen.</span>
                        </div>
                        {messages.length === 0 && (
                            <div className="text-center py-6">
                                <Sparkles className="w-8 h-8 text-[#FFCC00] mx-auto mb-2" />
                                <p className="text-sm font-bold text-gray-700">{pageCtx ? pageCtx.greeting : "Wie kann ich helfen?"}</p>
                                {pageCtx && (
                                    <p className="text-[11px] font-bold text-[#111827] mt-1.5 inline-flex items-center gap-1 bg-[#FFCC00]/20 px-2 py-0.5 rounded-full" data-testid="chatbot-page-context">
                                        <Sparkles className="w-3 h-3" /> Passend zu dieser Seite
                                    </p>
                                )}
                                <p className="text-xs text-gray-500 mt-1 px-4">Stellen Sie Ihre Frage – oder besprechen Sie Ihr Anliegen lieber per Sprache:</p>
                                {showTranscribe && (
                                    <div className="flex items-center justify-center gap-1.5 mt-2 text-[11px] text-gray-500">
                                        <Mic className="w-3.5 h-3.5" /> Tippen Sie unten auf das Mikrofon.
                                    </div>
                                )}
                                <div className="flex flex-col items-center gap-2 mt-3">
                                    <button
                                        onClick={startHandsFree}
                                        className={`inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full px-3 py-1.5 transition-colors ${voiceListening ? "bg-red-600 text-white" : "bg-[#111827] text-[#FFCC00] hover:bg-black"}`}
                                        data-testid="chatbot-handsfree-start"
                                    >
                                        <Mic className="w-3.5 h-3.5" />
                                        {voiceListening ? "Ich höre zu …" : "Freihand-Gespräch starten"}
                                    </button>
                                    <button
                                        onClick={toggleVoiceWake}
                                        className={`inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full border px-3 py-1.5 transition-colors ${voiceWake ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-[#111827] border-gray-300 hover:border-[#111827]"}`}
                                        data-testid="chatbot-wake-enable"
                                    >
                                        <Radio className="w-3.5 h-3.5" />
                                        {voiceWake ? `Sprachaktivierung „${wakeWord}“ aktiv` : `Freihändig: „${wakeWord}“ aktivieren`}
                                    </button>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <span className="text-[10px] text-gray-500 font-semibold">Aktivierungswort:</span>
                                        <input
                                            value={wakeWord}
                                            onChange={(e) => setWakeWord(e.target.value)}
                                            className="w-28 text-[11px] border border-gray-300 rounded-sm px-1.5 py-0.5 text-center font-bold focus:outline-none focus:ring-1 focus:ring-[#111827]"
                                            data-testid="chatbot-wakeword-input"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-wrap justify-center gap-1.5 mt-4 px-2" data-testid="chatbot-suggestions">
                                    {mergedSuggestions.map((s, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => pickSuggestion(s)}
                                            disabled={sending}
                                            className="text-[11px] font-medium px-2.5 py-1.5 rounded-full border border-gray-300 bg-white text-gray-700 hover:border-[#111827] hover:bg-gray-50 transition-colors disabled:opacity-50"
                                            data-testid={`chatbot-suggestion-${i}`}
                                        >
                                            {s.text}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chatbot-msg-${m.role}`}>
                                <div className={`max-w-[85%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-gray-200"}`}>
                                    {m.text}
                                    {m.actionId && !consumedActions[m.actionId] && (
                                        <div className="mt-2 flex gap-2">
                                            <button
                                                type="button"
                                                onClick={async () => { const r = await confirmPendingAction(m.actionId); if (voiceWakeRef.current) speak(r); }}
                                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-sm px-3 py-2 transition-colors"
                                                data-testid="chatbot-action-confirm"
                                            >
                                                ✓ Ausführen
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { pendingActionRef.current = null; setConsumedActions((c) => ({ ...c, [m.actionId]: true })); setMessages((mm) => [...mm, { role: "assistant", text: "Okay, verworfen." }]); }}
                                                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-black rounded-sm px-3 py-2 transition-colors"
                                                data-testid="chatbot-action-cancel"
                                            >
                                                Abbrechen
                                            </button>
                                        </div>
                                    )}
                                    {m.nav && (
                                        <button
                                            type="button"
                                            onClick={() => { navigate(m.nav.path); setOpen(false); }}
                                            className="mt-2 flex items-center gap-1.5 w-full justify-center bg-[#111827] text-[#FFCC00] text-xs font-black rounded-sm px-3 py-2 hover:bg-black transition-colors"
                                            data-testid="chatbot-nav-btn"
                                        >
                                            {m.nav.label} <ArrowRight className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    {m.role === "assistant" && m.text && m.text !== "…" && (
                                        <div className="mt-1.5 flex items-center gap-1 -mb-0.5" data-testid={`chatbot-feedback-${i}`}>
                                            {msgFeedback[i] ? (
                                                <span className="text-[10px] text-gray-400 inline-flex items-center gap-1">
                                                    {msgFeedback[i] === "up" ? <ThumbsUp className="w-3 h-3 text-emerald-500" /> : <ThumbsDown className="w-3 h-3 text-red-400" />}
                                                    Danke für dein Feedback!
                                                </span>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => sendFeedback(i, "up", m.text)}
                                                        className="p-1 rounded-sm text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                        title="Hilfreiche Antwort"
                                                        data-testid={`chatbot-thumb-up-${i}`}
                                                    >
                                                        <ThumbsUp className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => sendFeedback(i, "down", m.text)}
                                                        className="p-1 rounded-sm text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                        title="Nicht hilfreich"
                                                        data-testid={`chatbot-thumb-down-${i}`}
                                                    >
                                                        <ThumbsDown className="w-3 h-3" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {sending && <div className="flex justify-start"><div className="bg-white border border-gray-200 rounded-sm px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div></div>}
                        <div ref={endRef} />
                    </div>

                    <div className="p-3 border-t border-gray-200 shrink-0">
                        {voiceChips.length > 0 && (
                            <div className="mb-2" data-testid="chatbot-voice-chips">
                                <div className="flex items-center gap-1 mb-1">
                                    <Volume2 className="w-3 h-3 text-gray-400 shrink-0" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 truncate">Sprach-Schnellbefehle · Antwort wird vorgelesen</span>
                                </div>
                                <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                                    {voiceChips.map((c, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => runVoiceChip(c)}
                                            disabled={sending}
                                            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-full bg-[#111827] text-[#FFCC00] hover:bg-black transition-colors disabled:opacity-50"
                                            data-testid={`chatbot-voice-chip-${i}`}
                                        >
                                            <Volume2 className="w-3 h-3" /> {c.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {voiceWake && (
                            <div className="mb-2 flex items-center justify-between gap-2 text-[11px] font-bold rounded-sm px-2 py-1.5 bg-[#111827] text-white" data-testid="chatbot-voice-status">
                                <span className="inline-flex items-center gap-1.5 min-w-0 truncate">
                                    {voiceSpeaking ? (
                                        <><Volume2 className="w-3.5 h-3.5 text-[#FFCC00] animate-pulse shrink-0" /> KI spricht …</>
                                    ) : voiceListening ? (
                                        <><Mic className="w-3.5 h-3.5 text-red-400 animate-pulse shrink-0" /> Ich höre zu – stell deine Frage …</>
                                    ) : (
                                        <><Radio className="w-3.5 h-3.5 text-[#FFCC00] shrink-0" /> Sage „{wakeWord}“ …</>
                                    )}
                                </span>
                                {!voiceSpeaking && (voiceListening ? (
                                    <button
                                        type="button"
                                        onClick={endConvo}
                                        className="shrink-0 rounded-full bg-white/15 hover:bg-white/25 px-2 py-0.5 text-[10px] font-bold"
                                        data-testid="chatbot-convo-stop"
                                    >
                                        Zuhören stoppen
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={enterConvo}
                                        className="shrink-0 rounded-full bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 px-2 py-0.5 text-[10px] font-bold"
                                        data-testid="chatbot-convo-start"
                                    >
                                        Gespräch starten
                                    </button>
                                ))}
                            </div>
                        )}
                        {cta && (
                            <button
                                type="button"
                                onClick={goCta}
                                className="w-full mb-2 inline-flex items-center justify-center gap-1.5 h-9 rounded-sm bg-[#FFCC00] hover:bg-yellow-300 text-[#111827] text-sm font-bold transition-colors"
                                data-testid="chatbot-cta-btn"
                            >
                                {cta.label} <ArrowRight className="w-4 h-4" />
                            </button>
                        )}
                        <div className="flex items-end gap-2">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                rows={1}
                                placeholder="Nachricht …"
                                className="flex-1 rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#111827] resize-none max-h-24"
                                data-testid="chatbot-input"
                            />
                            {showTranscribe && <VoiceRecordButton onText={onVoice} disabled={sending} />}
                            <button onClick={() => send()} disabled={sending || !input.trim()} className="h-10 w-10 flex items-center justify-center rounded-sm bg-[#111827] hover:bg-black text-white disabled:opacity-50 shrink-0" data-testid="chatbot-send">
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
